import {
  Fragment,
  MutableRefObject,
  ReactNode,
  Ref,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react'

import { forEach, map } from 'lodash'

import {
  faChevronDown,
  faDownload,
  faLock,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import Button from 'Root/App/Button'
import CoverImage from 'Root/App/CoverImage'
import Buy from 'Root/App/Header/Buttons/Buy'
import Certificate from 'Root/App/Item/Certificate'
import Chip from 'Root/App/Item/Chip'
import CollectionInfo from 'Root/App/Item/CollectionInfo'
import UserProfile from 'Root/App/Item/UserProfile'
import Paragraph from 'Root/App/Paragraph'
import PreTitle from 'Root/App/PreTitle'
import List from 'Root/App/Tag/List'
import Title from 'Root/App/Title'
import UpdateCover from 'Root/Component/UpdateCover'
import RegistrateForContents from 'Root/Component/registrate-for-contents'
import Benefits from 'Root/Pages/Collection/Component/Benefits'
import Contents from 'Root/Pages/Collection/Component/Contents'
import ReviewComment from 'Root/Pages/Collection/Component/Contents/Review/Comment'
import ReviewInfo from 'Root/Pages/Collection/Component/Contents/Review/Info'
import UserBio from 'Root/Pages/Collection/Component/Contents/UserBio'
import Faq from 'Root/Pages/Collection/Component/Faq'
import Presentation from 'Root/Pages/Collection/Component/Presentation'
import { DEFAULT_COLLECTION_COVER_IMAGE } from 'lib/constants'
import {
  getPromotion,
  isCollectionInPromotion,
  hasPromoBenefits,
  hasPromoFAQ,
} from 'lib/get-promotion-data'
import { needRegistrationToViewContents } from 'lib/need-registration-to-view-contents'
import { showCollectionSellingInfo } from 'lib/show-selling-info'

import styles from './index.module.css'

const HorizontalBuy = ({ collection }) => {
  const { i18n } = useLingui()

  const discountData = {
    'iEOTG-S4AEF': {
      totalDiscount: '88%',
      originalPrice: '79,99 €',
    },
    'zfMK5-iDU0o': {
      totalDiscount: '76%',
      originalPrice: '79,99 €',
    },
    '5xRMH-VS5Yj': {
      totalDiscount: '60%',
      originalPrice: '24,99 €',
    },
  }

  const isStudent = collection?.canRead && !collection?.canManage

  if (
    !showCollectionSellingInfo(
      Boolean(collection?.selling),
      collection?.sellingPricing,
      collection?.sellingCurrency,
      isStudent,
      collection?.need_registration
    )
  ) {
    return null
  }

  const DiscountElement = () => {
    if (!discountData[collection.slug]) return null

    return (
      <>
        {discountData[collection.slug].totalDiscount} di sconto{' '}
        <s>{discountData[collection.slug].originalPrice}</s>
      </>
    )
  }

  return (
    <div className={'flex gap-6 mb-3'}>
      <div>
        <Title size="M" className="text-gray-900">
          {i18n.number(collection.sellingPricing, {
            style: 'currency',
            currency: collection.sellingCurrency,
          })}
        </Title>

        {isCollectionInPromotion(collection.slug) && (
          <PreTitle size="XS" className="text-gray-900">
            <DiscountElement />
          </PreTitle>
        )}
      </div>

      {!collection.canManage && (
        <Buy
          customClass={'min-w-24'}
          collection={collection}
          size={isCollectionInPromotion(collection.slug) ? 'M' : 'S'}
          pagePosition="price"
        />
      )}
    </div>
  )
}

const CollectionComments = forwardRef(function Footer(
  { children, total }: { children: ReactNode; total: number },
  ref: Ref<HTMLDivElement>
) {
  return (
    <div ref={ref} className={styles.commentsWrapper}>
      <Title>{`${total} Commenti degli studenti`}</Title>
      <ul className={styles.commentsList}>{children}</ul>
    </div>
  )
})

const CollectionFeatures = ({ children }) => (
  <div className="elative flex flex-col p-6 gap-3">
    <Title>Questo corso include</Title>
    {children}
  </div>
)

const StaticShowMore = () => (
  <button className="flex gap-3 items-center justify-center text-brand-main font-bold">
    <Paragraph className={styles.staticShowMoreLabel}>Leggi di più</Paragraph>
    <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3 text-brand-main" />
  </button>
)

const Footer = forwardRef(function Footer(
  { children }: { children: ReactNode },
  ref: Ref<HTMLDivElement>
) {
  return (
    <div ref={ref} className="mt-6 mb-6 rounded-3xl bg-gray-100">
      {children}
    </div>
  )
})

const getCollectionPageCover = (collection) => {
  const coverImage = (
    <CoverImage
      imageUrl={collection.cover || DEFAULT_COLLECTION_COVER_IMAGE}
      dimensions={collection.coverDimensions}
    />
  )

  if (collection.canManage) {
    return <UpdateCover id={collection.id}>{coverImage}</UpdateCover>
  }

  return coverImage
}

const DefaultStatus = ({ collection, me, refetch }) => {
  const userBioRef = useRef(null)
  const commentsListRef = useRef(null)
  const footerRef = useRef(null)
  const [isCollectionCompleted, setIsCollectionCompleted] = useState(false)
  const { comments, comments_count, faqList } = getPromotion(collection.slug)
  const hasContents =
    collection?.contents?.edges.length > 0 ||
    collection?.modules?.edges.length > 0

  useEffect(() => {
    if (!!collection) {
      if (collection.modules?.edges?.length > 0) {
        const modules = map(collection.modules?.edges || [], 'node')
        let modulesCompleted = 0
        forEach(modules, (module) => {
          const contents = map(module?.contents?.edges || [], 'node')

          let completed = 0
          forEach(contents, (content) => {
            if (
              content.hasUserVisited !== undefined &&
              content.hasUserVisited
            ) {
              completed++
            }
            if (
              content.hasUserCompleted !== undefined &&
              content.hasUserCompleted
            ) {
              completed++
            }
            if (!!checkEventPresence(content)) {
              completed++
            }
          })
          if (completed === contents.length) {
            modulesCompleted++
          }
        })
        setIsCollectionCompleted(modulesCompleted === modules.length)
      } else {
        const contents = map(collection?.contents?.edges || [], 'node')
        let completed = 0
        forEach(contents, (content) => {
          if (content.hasUserVisited !== undefined && content.hasUserVisited) {
            completed++
          }
          if (
            content.hasUserCompleted !== undefined &&
            content.hasUserCompleted
          ) {
            completed++
          }
          if (!!checkEventPresence(content)) {
            completed++
          }
        })
        setIsCollectionCompleted(completed === contents.length)
      }
    }
  })

  function checkEventPresence(event) {
    const presence = event?.presences?.find((pres) => pres.user?.id === me?.id)
    if (!!presence) {
      return presence.presence
    }
    return false
  }

  function handleComponentWithRefClick(ref: MutableRefObject<any>) {
    if (typeof window !== 'undefined') {
      if (ref.current) {
        window.scrollTo({
          top: ref.current.offsetTop,
          behavior: 'smooth',
        })
      }
    }
  }

  const showRegisterForContents = needRegistrationToViewContents(
    collection.canManage,
    collection.canRead,
    collection.need_registration
  )

  return (
    <Fragment>
      <div className={styles.wrapper}>
        {getCollectionPageCover(collection)}

        <div className={styles.subwrapper}>
          <PreTitle size="M">{collection.label}</PreTitle>
          <Title size="M">{collection.title}</Title>
          <Paragraph size="L">{collection.description}</Paragraph>

          <HorizontalBuy collection={collection} />

          <List tags={collection.tags} />
          {!isCollectionInPromotion(collection.slug) && (
            <CollectionInfo {...collection} />
          )}

          {!isCollectionInPromotion(collection.slug) &&
            collection.certificate_id && <Certificate />}

          {!collection.canManage && (
            <UserProfile
              {...collection.creator}
              anchorCallback={
                hasContents
                  ? () => handleComponentWithRefClick(userBioRef)
                  : null
              }
              isOnCollection
            />
          )}

          {showRegisterForContents && (
            <Chip
              icon={
                <div className="inline-flex items-center justify-center w-10 h-10 text-white bg-brand-main rounded-md">
                  <FontAwesomeIcon icon={faLock} className="w-4 h-4" />
                </div>
              }
              highlight
              highlightCustomClass="p-6 gap-3 flex flex-col items-center mt-9"
            >
              <span className="text-center">
                <Title size="XS" className="text-gray-900">
                  {collection?.creator?.name ? (
                    <Trans>
                      {`Accedi per visualizzare questo contenuto di 
                      ${collection.creator.name}.`}
                    </Trans>
                  ) : (
                    <Trans>Accedi per visualizzare questo contenuto.</Trans>
                  )}
                </Title>
                <Paragraph className="text-gray-900">
                  <Trans>
                    Bastano pochi secondi per accedere o creare il tuo account.
                  </Trans>
                </Paragraph>
              </span>

              <RegistrateForContents
                elementId={collection.id}
                className="bg-gray-900"
              >
                <Trans>Accedi</Trans>
              </RegistrateForContents>
            </Chip>
          )}

          {!collection.canManage && comments.length > 0 && (
            <ReviewInfo
              comments={comments_count}
              onClick={() => handleComponentWithRefClick(commentsListRef)}
            />
          )}
          {isCollectionCompleted &&
            !!collection.certificate_id &&
            !collection.canManage && (
              <Button
                className="bg-brand-accent2"
                size="L"
                anchor
                target="_blank"
                to={`/certificate/${collection.certificate_id}/course/${collection.id}`}
              >
                <div className="flex items-center gap-x-2">
                  <Trans>Scarica Certificato</Trans>
                  <FontAwesomeIcon icon={faDownload} />
                </div>
              </Button>
            )}
        </div>
      </div>

      {collection.presentation &&
        typeof collection.presentation === 'string' &&
        collection.presentation.length > 0 && (
          <div className="flex flex-col p-6 gap-y-3">
            <Presentation html={collection.presentation} />
          </div>
        )}

      <Contents {...collection} refetchOnUpdate={refetch} me={me} />

      {isCollectionInPromotion(collection.slug) && !collection.canManage && (
        <CollectionFeatures>
          <CollectionInfo {...collection} />

          <Certificate
            customClass="cursor-pointer"
            onClick={() => handleComponentWithRefClick(footerRef)}
          />
        </CollectionFeatures>
      )}

      {!collection.canManage && hasContents && (
        <UserBio ref={userBioRef} creator={collection.creator} />
      )}

      {!collection.canManage && comments.length > 0 && (
        <CollectionComments ref={commentsListRef} total={comments_count}>
          {comments.map((comment) => (
            <li key={comment.id}>
              <ReviewComment
                author={comment.author}
                created_at={comment.created_at}
                content={comment.content}
              />
            </li>
          ))}
          {collection.slug !== 'JNg34-trqvq' && <StaticShowMore />}
        </CollectionComments>
      )}

      {Boolean(collection.selling && !collection.canRead) && hasContents && (
        <Buy
          collection={collection}
          size="L"
          customClass="w-full"
          pagePosition="middle"
        />
      )}

      {isCollectionInPromotion(collection.slug) && !collection.canManage && (
        <>
          <Footer ref={footerRef}>
            {hasPromoBenefits(collection.slug) && (
              <Benefits creatorName={collection.creator.name} />
            )}
            {hasPromoFAQ(collection.slug) && (
              <Faq
                faqList={faqList}
                collectionSlug={collection.slug}
                collectionTitle={collection.title}
              />
            )}
          </Footer>

          {Boolean(collection.selling && !collection.canRead) && (
            <Buy
              collection={collection}
              size="L"
              customClass="w-full"
              pagePosition="bottom"
            />
          )}
        </>
      )}
    </Fragment>
  )
}

export default DefaultStatus
